import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@artstorefronts/ui';
import { useTranslation } from 'react-i18next';

interface BlockMemberDialogProps {
  open: boolean;
  /** Which way the entry point currently reads, from ConnectionState.isBlockedByViewer. */
  mode: 'block' | 'unblock';
  /** Shown in the title. Fall back to social:block.this-member when no name is in scope. */
  name: string;
  pending?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * The one block/unblock confirmation for the SPA — the connections list and the
 * thread menu render this, so both promise exactly the same thing. Its
 * counterpart in the Next app is town-square/src/components/social/BlockMemberDialog.tsx;
 * the copy is shared through the `social.block` catalog namespace, the markup is
 * not, because the two apps have different i18n and dialog idioms.
 */
export function BlockMemberDialog({ open, mode, name, pending = false, onCancel, onConfirm }: BlockMemberDialogProps) {
  const { t } = useTranslation('social');
  const isUnblock = mode === 'unblock';

  function handleOpenChange(next: boolean) {
    if (next) return;
    onCancel();
  }

  function renderBody() {
    if (isUnblock) return null;
    return (
      <>
        <ul className="list-disc space-y-1 pl-5 text-sm text-gray-600">
          <li>{t('block.bullet-messages', 'No direct messages, connection requests or recommendations')}</li>
          <li>{t('block.bullet-comments', 'No comments on your artwork or your posts, and no likes')}</li>
          <li>{t('block.bullet-follows', 'You both stop following each other, and your connection ends')}</li>
          <li>{t('block.bullet-silent', 'They are not told, and they can still see anything you made public')}</li>
        </ul>
        {/* Names the one reach the block deliberately does not have — a group
            thread the pair already share — rather than promising "anywhere". */}
        <p className="text-xs text-gray-500">
          {t(
            'block.footnote',
            'Comments they already left stay up until you delete them. In a group chat you both share with other members they may still be able to post, and you stop being notified. You can lift this in Blocked members.'
          )}
        </p>
      </>
    );
  }

  const title = isUnblock
    ? t('block.unblock-title', 'Unblock {{name}}?', { name })
    : t('block.title', 'Block {{name}}?', { name });
  const description = isUnblock
    ? t(
        'block.unblock-body',
        'Unless they have blocked you too, they will be able to send you a connection request and comment on your artwork and your posts again. Unblocking restores nothing else: it does not bring back a connection or a follow, and a direct message needs a connection first.'
      )
    : t('block.lead', 'They will not be able to reach you on ArtHelper.');
  const confirmLabel = isUnblock ? t('block.unblock-confirm', 'Unblock member') : t('block.confirm', 'Block member');

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {renderBody()}
        <AlertDialogFooter>
          {/* No onClick: Radix's Cancel closes the dialog, which already runs
              handleOpenChange -> onCancel. Wiring both fires the caller's cancel
              analytics twice. */}
          <AlertDialogCancel disabled={pending}>{t('common:cancel', 'Cancel')}</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
            disabled={pending}
            className={isUnblock ? undefined : 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500'}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
